/**
 * Caret arithmetic over already-shaped text.
 *
 * Every function here takes a ShapedLine and returns a number. None of them
 * shapes anything, and that is the point: this module must not import HarfBuzz,
 * because everything that touches HarfBuzz is pulled into whichever chunk
 * imports it, and HarfBuzz is 174 KB of WASM behind a top-level await.
 *
 * They lived on the Shaper class first, which put the whole shaping engine into
 * the editor's entry chunk and made the shell wait for it before painting -
 * exactly the thing §18 and the phase 0 findings both warn about. Only the
 * types come from shaper.ts now, and types are erased.
 */

import type { ShapedLine, ShapedParagraph } from './shaper';

/**
 * Which line a character index falls on.
 *
 * Clamped - a caret past the end belongs on the last line, not nowhere.
 */
export function lineOf(paragraph: ShapedParagraph, index: number): number {
  for (let i = 0; i < paragraph.lines.length; i++) {
    const line = paragraph.lines[i]!;

    if (index >= line.start && index <= line.end) return i;
  }

  return Math.max(0, paragraph.lines.length - 1);
}

/**
 * The character positions a caret may occupy on this line, ascending.
 *
 * Not every index is one. HarfBuzz merges characters into a single cluster when
 * they shape together - in Cairo, the first two letters of شركة come back as
 * one - and a caret cannot sit inside a cluster any more than it can sit inside
 * a letter. Asking the shaping which positions exist is the only correct
 * answer; guessing produces a caret that vanishes to the far end of the line.
 */
export function boundaries(line: ShapedLine): number[] {
  const found = new Set<number>([line.start, line.end]);

  for (const glyph of line.glyphs) found.add(glyph.cluster);

  return Array.from(found).sort((a, b) => a - b);
}

/**
 * The nearest position a caret may actually occupy.
 *
 * @param prefer Which way to go when the index is inside a cluster. Moving with
 *               the arrow keys wants 'forward' or 'back', so the caret steps
 *               over the cluster rather than sticking to its near edge; a click
 *               wants 'near'.
 */
export function snapIndex(
  line: ShapedLine,
  index: number,
  prefer: 'forward' | 'back' | 'near' = 'near'
): number {
  const stops = boundaries(line);

  if (stops.includes(index)) return index;

  if (prefer === 'forward') {
    return stops.find((stop) => stop > index) ?? stops[stops.length - 1] ?? index;
  }

  if (prefer === 'back') {
    return [...stops].reverse().find((stop) => stop < index) ?? stops[0] ?? index;
  }

  return stops.reduce(
    (best, stop) => (Math.abs(stop - index) < Math.abs(best - index) ? stop : best),
    stops[0] ?? index
  );
}

/**
 * Where a caret sits, for a position between characters.
 *
 * In right-to-left text the caret for index 0 is on the right, so this cannot
 * sum the advances before it - it has to ask the glyph which way its run runs.
 */
export function caretX(line: ShapedLine, index: number): number {
  if (line.glyphs.length === 0) return line.offset;

  const at = snapIndex(line, index);

  for (const glyph of line.glyphs) {
    if (glyph.cluster === at) {
      return line.offset + (glyph.rtl ? glyph.x + glyph.advance : glyph.x);
    }
  }

  // Only the end of the line reaches here, since every other stop came from a
  // glyph. It sits past the last glyph in logical order.
  let last = line.glyphs[0]!;

  for (const glyph of line.glyphs) {
    if (glyph.cluster > last.cluster) last = glyph;
  }

  return line.offset + (last.rtl ? last.x : last.x + last.advance);
}

/**
 * The character position nearest a horizontal distance along a line.
 *
 * This is what turns a click into a caret. It cannot be "count the advances
 * until you pass x", because in right-to-left text the first character is on
 * the right: each glyph is asked where its own leading edge is, and the nearest
 * of those wins.
 *
 * @param x Distance from the line's own origin, in font units.
 */
export function indexAt(line: ShapedLine, x: number): number {
  if (line.glyphs.length === 0) return line.start;

  const local = x - line.offset;

  let nearest = line.start;
  let distance = Infinity;

  for (const glyph of line.glyphs) {
    // Both edges of every glyph are candidates, so a click on the far half of a
    // letter puts the caret after it - in whichever direction "after" means for
    // that run.
    const edges: Array<[number, number]> = glyph.rtl
      ? [
          [glyph.x + glyph.advance, glyph.cluster],
          [glyph.x, glyph.cluster + 1],
        ]
      : [
          [glyph.x, glyph.cluster],
          [glyph.x + glyph.advance, glyph.cluster + 1],
        ];

    for (const [at, index] of edges) {
      const gap = Math.abs(local - at);

      if (gap < distance) {
        distance = gap;
        nearest = index;
      }
    }
  }

  return Math.max(line.start, Math.min(line.end, nearest));
}

/** Boxes covering a logical range, one per stretch of adjacent glyphs. */
export function selectionBoxes(
  line: ShapedLine,
  from: number,
  to: number
): Array<{ x: number; width: number }> {
  const [lo, hi] = from <= to ? [from, to] : [to, from];

  const inRange = line.glyphs.filter((g) => g.cluster >= lo && g.cluster < hi).sort((a, b) => a.x - b.x);
  const boxes: Array<{ x: number; width: number }> = [];

  for (const glyph of inRange) {
    const last = boxes[boxes.length - 1];

    // Adjacent on screen: extend rather than start a new box, so a
    // right-to-left selection is one rectangle and not one per letter.
    if (last && Math.abs(last.x + last.width - glyph.x) < 1) {
      last.width += glyph.advance;
    } else {
      boxes.push({ x: line.offset + glyph.x, width: glyph.advance });
    }
  }

  return boxes;
}
